import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuLogOut, LuMapPin, LuClock, LuMessageCircle, LuBellRing, LuTriangleAlert, LuSun, LuMoon, LuCamera, LuStar, LuX } from 'react-icons/lu';
import api from '../../services/api';
import logo from '../../assets/logo-dispenser.svg';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { useArrastrarHojas } from '../../hooks/useArrastrarHojas';
import { Segmentado, CONTENEDOR, PAGINA } from '../ui/Pantalla';
import NotificacionesPanel, { NotifBell } from '../layout/NotificacionesPanel';
import { sonarAviso } from '../layout/AvisoUrgente';
import { pushSoportado, activarNotificaciones } from '../../utils/pushNotifications';
import { estadoDe, cuandoPedido, haceCuanto } from '../../utils/pedidosEmpresa';
import PedidoDetalle from './PedidoDetalle';
import NuevoPedidoSheet from './NuevoPedidoSheet';
import ClientesEmpresa from './ClientesEmpresa';
import ResumenMes from './ResumenMes';
import MantenimientosEmpresa from './MantenimientosEmpresa';
import IndicadoresEmpresa from './IndicadoresEmpresa';
import ListaPedidosEmpresa from './ListaPedidosEmpresa';

// Portal Empresa (7-oct-2026): lo único que ve un usuario EMPRESA. Carga
// pedidos, sigue el estado de cada uno y conversa con Dispenser La Tienda en el
// mismo pedido. Reemplaza el Trello + WhatsApp. No ve precios, otros clientes,
// técnicos ni nada interno (el backend se lo corta igual).
export default function PortalEmpresa() {
    const { usuario, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [empresa, setEmpresa] = useState('');
    const [miLugar, setMiLugar] = useState(null); // encargado de un lugar (8-oct-2026)
    // Funciones que el admin habilitó para este cliente (9-oct-2026)
    const [funciones, setFunciones] = useState({ calificacion: false, numeros: false, mapa: true, comentarios: true });
    const [periodoResumen, setPeriodoResumen] = useState(null); // 'semana' al abrir el reporte semanal
    const [pedidos, setPedidos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [abierto, setAbierto] = useState(null);
    const [nuevo, setNuevo] = useState(false); // false | true | { sedeId, serie }
    const [vista, setVista] = useState('pedidos');
    const [notifCount, setNotifCount] = useState(0);
    const [notifAbierto, setNotifAbierto] = useState(false);
    const [sinBanner, setSinBanner] = useState(() => { try { return localStorage.getItem('empresa_sin_banner_avisos') === '1'; } catch { return false; } });
    const cerrarBanner = () => { setSinBanner(true); try { localStorage.setItem('empresa_sin_banner_avisos', '1'); } catch { /* sin storage */ } };
    const [permiso, setPermiso] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'denied'));
    const ultimoCount = useRef(null);
    useArrastrarHojas(); // celular: cerrar ventanas arrastrándolas hacia abajo

    const cargar = useCallback(async () => {
        try {
            const r = await api.get('/empresa/pedidos');
            setPedidos(Array.isArray(r.data) ? r.data : []);
        } catch { /* sin señal: queda lo que había */ } finally { setCargando(false); }
    }, []);

    const pollNotifs = useCallback(async () => {
        try {
            const r = await api.get('/notificaciones/count');
            const c = r.data?.count || 0;
            if (ultimoCount.current !== null && c > ultimoCount.current) { sonarAviso(); cargar(); }
            ultimoCount.current = c;
            setNotifCount(c);
        } catch { /* */ }
    }, [cargar]);

    useEffect(() => {
        api.get('/empresa/datos').then(r => {
            setEmpresa(r.data?.empresa || '');
            setMiLugar(r.data?.sedeId ? { id: r.data.sedeId, nombre: r.data.sedeNombre } : null);
            if (r.data?.funciones) setFunciones(r.data.funciones);
        }).catch(() => {});
        cargar(); pollNotifs();
        const a = setInterval(cargar, 30000), b = setInterval(pollNotifs, 15000);
        return () => { clearInterval(a); clearInterval(b); };
    }, [cargar, pollNotifs]);

    const abrirPedidoId = useCallback(async (id) => {
        try { const r = await api.get(`/empresa/pedidos/${id}`); setAbierto(r.data); } catch { toast.error('No se encontró el pedido'); }
    }, []);

    // Abierto desde un push: /?notif=1&pedido=12 (o &ordenId=…)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('notif') !== '1') return;
        const pid = params.get('pedido'), oid = params.get('ordenId');
        if (pid) abrirPedidoId(pid);
        else if (oid) api.get(`/empresa/pedidos/por-orden/${oid}`).then(r => setAbierto(r.data)).catch(() => setNotifAbierto(true));
        else setNotifAbierto(true);
        window.history.replaceState({}, '', window.location.pathname);
    }, [abrirPedidoId]);

    // Pedir servicio para varios equipos (11-oct-2026): un pedido por lugar, uno detrás del otro
    const cola = useRef([]);
    const pedirServicio = useCallback((equipos, motivo = null) => {
        const porLugar = new Map();
        equipos.forEach(e => { if (!porLugar.has(e.sedeId)) porLugar.set(e.sedeId, []); porLugar.get(e.sedeId).push(e.serie); });
        const pedidosNuevos = [...porLugar.entries()].map(([sedeId, series]) => ({ sedeId, series, motivo }));
        cola.current = pedidosNuevos.slice(1);
        if (pedidosNuevos.length > 1) toast(`Son ${pedidosNuevos.length} lugares: armamos un pedido por lugar`, { icon: '📍' });
        setNuevo(pedidosNuevos[0] || false);
    }, []);
    const pedidoCreado = (p) => {
        cargar();
        if (cola.current.length) {
            const [sig, ...resto] = cola.current;
            cola.current = resto;
            setNuevo(false);
            setTimeout(() => setNuevo(sig), 50);
            toast.success(`Listo. Ahora el pedido del siguiente lugar (quedan ${resto.length + 1})`);
            return;
        }
        setNuevo(false); setVista('pedidos'); setAbierto(p);
    };

    const activarPush = async () => {
        try { await activarNotificaciones(); setPermiso('granted'); toast.success('Listo, te van a llegar los avisos'); }
        catch (e) { setPermiso(typeof Notification !== 'undefined' ? Notification.permission : 'denied'); toast.error(e.message || 'No se pudieron activar'); }
    };

    // <main>: en compu toma zoom (index.css, .portal-empresa): el portal es una sola
    // columna angosta, así que se agranda más que la app del admin.
    return (
        <main className={`${PAGINA} portal-empresa`}>
            {/* Encabezado */}
            <header className="sticky top-0 z-30 bg-page border-b border-black/[0.06] dark:border-white/[0.06]">
                <div className="max-w-3xl mx-auto px-4 h-16 sm:h-[72px] flex items-center gap-2 sm:gap-3">
                    <img src={logo} alt="Dispenser La Tienda" className="h-9 sm:h-12 w-auto shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-body font-black text-ink truncate">{empresa || 'Portal empresa'}</p>
                        <p className="text-caption text-muted truncate">{usuario?.nombre}{miLugar?.nombre ? ` · ${miLugar.nombre}` : ''}</p>
                    </div>
                    <NotifBell count={notifCount} onClick={() => setNotifAbierto(true)} />
                    <button type="button" onClick={toggleTheme} aria-label="Cambiar tema" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl bg-chip text-secondary flex items-center justify-center">{isDark ? <LuSun size={17} /> : <LuMoon size={17} />}</button>
                    <button type="button" onClick={logout} aria-label="Salir" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl bg-chip text-secondary flex items-center justify-center"><LuLogOut size={17} /></button>
                </div>
            </header>

            <div className={CONTENEDOR.replace('max-w-6xl', 'max-w-3xl')}>
                {/* Aviso para activar notificaciones (13-oct-2026): una línea y se puede cerrar */}
                {pushSoportado() && permiso === 'default' && !sinBanner && (
                    <div className="flex items-center gap-2 pl-3.5 pr-1.5 h-12 rounded-2xl bg-[rgba(201,52,31,0.08)] border border-[rgba(201,52,31,0.2)]">
                        <LuBellRing size={17} className="text-brand-red shrink-0" />
                        <button type="button" onClick={activarPush} className="flex-1 min-w-0 text-left text-caption font-black text-ink truncate">
                            Activá los avisos <span className="font-bold text-secondary">· te contamos cuando vamos</span>
                        </button>
                        <button type="button" onClick={activarPush} className="shrink-0 h-8 px-3 rounded-lg bg-brand-red text-white text-label font-black">Activar</button>
                        <button type="button" onClick={cerrarBanner} aria-label="Ahora no" className="shrink-0 w-8 h-8 rounded-lg text-muted flex items-center justify-center"><LuX size={16} /></button>
                    </div>
                )}

                <div className="-mx-1 px-1 overflow-x-auto [&>div]:w-full [&>div]:min-w-[340px] [&_button]:flex-1 [&_button]:justify-center [&_button]:px-1 [&_button]:text-[12px] [&_button]:whitespace-nowrap">
                    <Segmentado valor={vista} onChange={setVista} opciones={[
                        { id: 'pedidos', label: 'Pedidos' },
                        { id: 'clientes', label: 'Clientes' },
                        { id: 'services', label: 'Services' },
                        { id: 'resumen', label: 'Resumen' },
                        ...(funciones.numeros ? [{ id: 'numeros', label: 'Números' }] : []),
                    ]} />
                </div>

                {vista === 'numeros' && funciones.numeros ? (
                    <IndicadoresEmpresa />
                ) : vista === 'services' ? (
                    <MantenimientosEmpresa pedidos={pedidos} onPedir={(lista) => pedirServicio(lista, 'Mantenimiento / limpieza')} />
                ) : vista === 'resumen' ? (
                    <ResumenMes empresa={empresa} periodoInicial={periodoResumen} />
                ) : vista === 'clientes' ? (
                    <ClientesEmpresa pedidos={pedidos} conMapa={funciones.mapa} soloMiLugar={!!miLugar} empresa={empresa}
                        onPedir={(sedeId) => setNuevo({ sedeId, series: [] })} onAbrirPedido={setAbierto} onRecargar={cargar}
                        onPedirEquipo={(eq) => pedirServicio([eq])} />
                ) : (
                    <ListaPedidosEmpresa pedidos={pedidos} cargando={cargando} funciones={funciones} puedeRuta={funciones.mapa && !miLugar}
                        onAbrir={setAbierto} onNuevo={() => setNuevo(true)} onRecargar={cargar} />
                )}
            </div>

            {nuevo && <NuevoPedidoSheet key={nuevo === true ? 'nuevo' : `${nuevo.sedeId}-${(nuevo.series || []).join(',')}`}
                inicial={nuevo === true ? null : nuevo} soloMiLugar={!!miLugar} puedeRuta={funciones.mapa && !miLugar}
                onCerrar={() => { cola.current = []; setNuevo(false); }} onCreado={pedidoCreado} />}
            {abierto && <PedidoDetalle key={abierto.id} pedido={abierto} modo="empresa" funciones={funciones} onCerrar={() => { setAbierto(null); cargar(); }} onCambio={cargar} />}
            <NotificacionesPanel abierto={notifAbierto}
                onCerrar={() => { setNotifAbierto(false); pollNotifs(); }}
                onAbrirPedido={(id) => { setNotifAbierto(false); abrirPedidoId(id); }}
                onSinReferencia={(n) => {
                    setNotifAbierto(false);
                    // Reporte semanal: abre Resumen en la semana pasada
                    if (String(n?.titulo || '').startsWith('📋')) { setPeriodoResumen('semana'); setVista('resumen'); }
                    else if (String(n?.titulo || '').startsWith('Resumen de')) { setPeriodoResumen('mes'); setVista('resumen'); }
                }} />
        </main>
    );
}

export function TarjetaPedido({ p, onClick, mostrarCliente = false, funciones = null }) {
    const obsPendiente = (p.conformidad === 'OBSERVADO' || p.conformidad === 'PROBLEMA') && !['RESUELTA', 'NO_CORRESPONDE'].includes(p.observacionEstado);
    const est = estadoDe(p);
    const cuando = cuandoPedido(p);
    return (
        <button type="button" onClick={onClick}
            className="w-full min-w-0 text-left p-4 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] shadow-sm active:scale-[0.99] space-y-2">
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <p className="text-[16px] font-black text-ink leading-tight">
                        {p.urgente && <LuTriangleAlert size={15} className="inline -mt-0.5 mr-1 text-brand-red" />}
                        {p.motivo}{p.equipoSerie ? <span className="text-muted font-bold"> · N/S {p.equipoSerie}</span> : null}
                    </p>
                    {mostrarCliente && p.clienteNombre && <p className="text-caption font-black text-secondary uppercase tracking-wide">{p.clienteNombre}</p>}
                </div>
                <span className="shrink-0 h-7 px-2.5 rounded-full inline-flex items-center gap-1.5 text-caption font-black"
                    style={{ background: `${est.color}1F`, color: est.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: est.color }} />{est.label}
                </span>
            </div>
            <p className="flex items-center gap-2 min-w-0 text-body text-secondary"><LuMapPin size={14} className="shrink-0 text-muted" /><span className="truncate min-w-0">{p.lugar && p.lugar !== p.direccion ? `${p.lugar} · ` : ''}{p.direccion}</span></p>
            <div className="flex items-center gap-3 text-body text-muted">
                {cuando ? <span className="inline-flex items-center gap-1.5 font-bold text-secondary"><LuClock size={13} />{cuando}</span> : <span>#{p.id} · {haceCuanto(p.creadoEn)}</span>}
                <span className="ml-auto inline-flex items-center gap-3">
                    {p.fotos?.length > 0 && <span className="inline-flex items-center gap-1"><LuCamera size={13} />{p.fotos.length}</span>}
                    {p.conformidad === 'PROBLEMA' && <span className="inline-flex items-center gap-1 font-black text-brand-red"><LuTriangleAlert size={13} />Reclamo</span>}
                    {p.conformidad === 'OBSERVADO' && (obsPendiente
                        ? <span className={`inline-flex items-center gap-1 font-black ${mostrarCliente ? 'text-brand-red' : 'text-[#A16207] dark:text-[#F0A500]'}`}><LuMessageCircle size={13} />{mostrarCliente ? 'Observación pendiente' : 'Comentario enviado'}</span>
                        : <span className="inline-flex items-center gap-1 font-black text-secondary"><LuMessageCircle size={13} />Respondido</span>)}
                    {p.conformidad === 'CONFORME' && <span className="inline-flex items-center gap-1 font-black text-[#16A34A]">✓{p.calificacion ? <><LuStar size={12} className="fill-current" />{p.calificacion}</> : ' Conforme'}</span>}
                    {/* Sin calificación no se muestra nada: el "Hecho" del costado ya lo dice */}
                    {p.estado === 'HECHO' && !p.conformidad && !mostrarCliente && funciones?.calificacion &&
                        <span className="font-black text-brand-red">Confirmá ›</span>}
                    {p.comentarios > 0 && <span className="inline-flex items-center gap-1"><LuMessageCircle size={13} />{p.comentarios}</span>}
                </span>
            </div>
        </button>
    );
}
